/**
 * Portuguese FAQ translations
 * Source of truth match - English structure
 * Last Updated: January 5, 2026 | Version 3.0
 */
export const faq = {
  // Page Meta
  pageTitle: "Perguntas Frequentes",
  heroSubtitle: "Tudo o que Você Precisa Saber Sobre o Observatório de Futuros da Juventude",
  heroDescription: "Respostas abrangentes sobre como o Observatório funciona, como o Índice de Futuros da Juventude foi desenvolvido e como você pode usar ou contribuir para esta plataforma.",
  lastUpdated: "Última Atualização: 5 de Janeiro de 2026",
  version: "Versão 3.0",
  availableLanguages: "Disponível em Inglês, Português, Espanhol, Francês e Japonês",

  // Section Navigation
  sectionUnderstanding: "Compreendendo",
  sectionMethodology: "Metodologia",
  sectionData: "Dados e Fontes",
  sectionUsing: "Usando o Observatório",
  sectionFuture: "Desenvolvimento Futuro",
  sectionEvolve: "Plano 2026",
  sectionCommon: "Perguntas Comuns",
  sectionContact: "Contato",

  // Section 1: Understanding the Observatory
  understandingTitle: "Compreendendo o Observatório",
  
  // Q1: What is the Youth Futures Observatory?
  q1Title: "O que é o Observatório de Futuros da Juventude?",
  q1Answer: "O Observatório de Futuros da Juventude é uma plataforma de inteligência baseada em evidências que combina dados verificados dos registros institucionais da JCI e pesquisas globais sobre juventude para mapear oportunidades para jovens em todo o mundo. Foi projetado para ajudar Organizações Nacionais e Locais da JCI a entender o contexto mais amplo em que operam e identificar onde seus esforços de desenvolvimento de liderança têm maior impacto.",
  q1Detail: "Em vez de simplesmente contar membros ou programas da JCI, o Observatório faz uma pergunta fundamental: Como a JCI contribui para expandir oportunidades para jovens em todo o mundo?",
  q1Integrates: "Ele responde a isso integrando:",
  q1IntegratesJCI: "Dados Institucionais da JCI — Membros reais, programas, participação de membros em 114 países",
  q1IntegratesGlobal: "Índices Globais de Oportunidade para Juventude — Dados comparativos sobre saúde mental, desenvolvimento da força de trabalho e empreendedorismo nos mesmos 114 países",
  q1IntegratesStrategic: "Documentação Estratégica — Metas da Visão 2040 e ambições de crescimento da JCI até 2040",
  q1Result: "O resultado é uma ferramenta estratégica que mostra à JCI onde expandir, o que priorizar e como medir o sucesso no avanço da oportunidade global para jovens.",

  // Q2: Why "Youth Futures"?
  q2Title: "Por que se chama Observatório 'Futuros da Juventude'?",
  q2Answer: "'Futuros da Juventude' reflete um conceito importante dos estudos de futuros — o campo acadêmico que examina como decisões e ações moldam futuros possíveis. O nome codifica três ideias-chave:",
  q2Idea1Title: "O Plural de 'Futuros'",
  q2Idea1: "Os jovens não têm um único futuro predeterminado. Eles têm múltiplos futuros possíveis dependendo das oportunidades disponíveis, acesso a recursos, qualidade da educação, suporte de saúde mental e força do ecossistema empreendedor.",
  q2Idea2Title: "A Perspectiva dos Estudos de Futuros",
  q2Idea2: "Os estudos de futuros nos ensinam que o futuro não é predeterminado — ele é moldado pelas escolhas que fazemos hoje. Ao analisar as condições atuais, estamos mapeando os futuros prováveis que os jovens herdarão. Ao identificar lacunas, estamos identificando onde a JCI pode intervir para criar futuros melhores.",
  q2Idea3Title: "Tomada de Decisão Baseada em Dados",
  q2Idea3: "Entender esses futuros requer dados rigorosos sobre o que está acontecendo agora em cada região, para que os líderes possam tomar decisões informadas sobre onde seus esforços terão maior impacto.",
  q2Summary: "O Observatório dá voz à realidade de que a oportunidade para jovens não está distribuída uniformemente globalmente. Um jovem na Europa enfrenta paisagens de oportunidade vastamente diferentes de um jovem na África e Oriente Médio.",

  // Q3: Why was it created?
  q3Title: "Por que os Conselhos de Desenvolvimento da JCI criaram este Observatório?",
  q3Answer: "O 4º Objetivo Estratégico do Plano Estratégico 2023-2027 da JCI é Inovação Baseada em Dados. Este objetivo afirma: 'Para ser uma organização líder, precisamos estar na vanguarda da tecnologia e inovação.'",
  q3Questions: "O Observatório foi criado para responder três perguntas:",
  q3Question1: "Como provamos nosso impacto? — A JCI precisava de uma forma rigorosa e auditável de demonstrar impacto através de dados, não apenas histórias.",
  q3Question2: "Onde devemos focar nossos esforços? — Com 4.641 Organizações Locais em 114 países, a JCI precisa de inteligência estratégica sobre onde os esforços têm maior impacto.",
  q3Question3: "Como nos alinhamos com prioridades globais? — Ao mapear o trabalho da JCI contra índices globais de oportunidade para jovens, a JCI pode demonstrar alinhamento com os ODS da ONU e se posicionar como parceiro estratégico.",
  q3Conclusion: "O Observatório é Inovação Baseada em Dados na prática — usando dados verificados e análise sistemática para guiar decisões estratégicas.",

  // Characteristics
  characteristics: {
    dataBased: "Baseado em Dados Reais",
    dataBasedDesc: "Apenas dados verificáveis e auditáveis",
    transparent: "Transparência Total",
    transparentDesc: "Metodologia totalmente exposta",
    collaborative: "Colaborativo",
    collaborativeDesc: "Construído com contribuições de toda a comunidade JCI",
    accessible: "Acessível",
    accessibleDesc: "Gratuito e aberto para uso não comercial",
    scientific: "Científico",
    scientificDesc: "Alinhado com padrões internacionais de pesquisa",
    inclusive: "Inclusivo",
    inclusiveDesc: "Reflete a diversidade global da JCI",
  },

  // Section 2: Observatory Methodology
  methodologyTitle: "Metodologia do Observatório",

  // Q4: How was the YFI developed?
  q4Title: "Como o Índice de Futuros da Juventude foi desenvolvido?",
  q4Answer: "O Índice de Futuros da Juventude (YFI) não é uma nova invenção criada para a JCI. É uma síntese e adaptação de metodologias de pesquisa global estabelecidas que medem oportunidade para jovens.",
  q4Steps: "Veja como foi desenvolvido:",
  q4Step1Title: "Passo 1: Identificou Estruturas Existentes",
  q4Step1: "Equipes de pesquisa revisaram índices de oportunidade para jovens existentes de instituições globais: Atlas de Saúde Mental da OMS, bancos de dados de emprego da OIT, estatísticas de educação da UNESCO, estudos do Monitor Global de Empreendedorismo.",
  q4Step2Title: "Passo 2: Projetou a Estrutura do YFI",
  q4Step2: "Essas dimensões foram integradas em um único índice composto usando ponderação baseada em pesquisa:",
  q4Weight1: "Saúde Mental: 40% (acesso a serviços, taxas de prevalência, mortalidade por suicídio)",
  q4Weight2: "Força de Trabalho e Educação: 30% (desemprego juvenil, taxas NEET, desenvolvimento de habilidades)",
  q4Weight3: "Empreendedorismo: 30% (ambiente de negócios, acesso a financiamento, capacidade de inovação)",
  q4WeightRationale: "A ponderação reflete pesquisas mostrando que a oportunidade para jovens depende mais fortemente da saúde mental (que habilita todas as outras capacidades), seguida da prontidão para o trabalho, depois da capacidade empreendedora.",
  q4Step3Title: "Passo 3: Aplicou aos 114 Países da JCI",
  q4Step3: "A estrutura do YFI foi calculada para todos os 114 países onde a JCI opera, criando um mapa global de oportunidade para jovens que se alinha diretamente com a presença geográfica da JCI.",
  q4Step4Title: "Passo 4: Validou os Resultados",
  q4Step4: "Os resultados foram verificados cruzadamente contra feedback de membros da JCI das pesquisas de 2025 e pesquisa global publicada sobre desenvolvimento juvenil.",
  q4Result: "O Resultado: Um índice rigoroso e auditável fundamentado em pesquisa estabelecida, não especulação ou modelos.",

  // YFI Formula
  yfiFormulaTitle: "Fórmula do YFI",
  yfiFormula: "YFI = (0,40 × SM) + (0,30 × FT) + (0,30 × EMP)",
  yfiMH: "SM = Componente de Saúde Mental (peso de 40%)",
  yfiWF: "FT = Componente de Força de Trabalho e Educação (peso de 30%)",
  yfiENT: "EMP = Componente de Empreendedorismo (peso de 30%)",

  // Q5: Dashcity Methodology
  q5Title: "Como funciona a Metodologia de 5 Fases Dashcity?",
  q5Answer: "A Metodologia de 5 Fases Dashcity fornece a estrutura estratégica de como o Observatório foi construído e como evoluirá. Cada fase tem um objetivo específico:",
  
  phase1: "IDENTIFICAR",
  phase1Status: "Concluído",
  phase1Dates: "Set-Nov 2025",
  phase1Objective: "Mapear o ecossistema completo de liderança juvenil",
  phase1Activities: "Analisou o Plano Estratégico da JCI 2023-2027; Diagnosticou o cenário institucional (114 países, 4.641 organizações locais, 148.122 membros); Identificou a lacuna: dados da JCI existiam em silos sem análise unificada",
  phase1Deliverable: "Estrutura estratégica e escopo de pesquisa",

  phase2: "MEDIR",
  phase2Status: "Concluído",
  phase2Dates: "Out-Dez 2025",
  phase2Objective: "Estabelecer padrões rigorosos de dados e processos de validação",
  phase2Activities: "Estabeleceu 7 critérios de qualidade inegociáveis; Verificou todas as fontes de dados primários (Relatório de Impacto da JCI, pesquisas, Visão 2040); Integrou fontes de dados globais (OMS, OIT, UNESCO, Banco Mundial, GEM); Implementou processo de validação de 5 passos",
  phase2Deliverable: "Arquitetura de dados e padrões de qualidade",

  phase3: "PROMOVER",
  phase3Status: "Em Andamento",
  phase3Dates: "Dez 2025-Jan 2026",
  phase3Objective: "Comunicar valor e engajar partes interessadas",
  phase3Activities: "Desenvolveu canais de engajamento de partes interessadas; Construiu diretrizes de contribuição e estruturas de incentivo; Produziu recursos multilíngues (EN, PT, ES, FR, JA)",
  phase3Deliverable: "Kit de comunicação e infraestrutura de engajamento",

  phase4: "DIRECIONAR",
  phase4Status: "Em Andamento",
  phase4Dates: "Jan-Fev 2026",
  phase4Objective: "Estabelecer governança e supervisão de qualidade",
  phase4Activities: "Documentou processos de validação (portal de qualidade de 7 passos); Definiu ciclos de atualização (anual, intermediário, contínuo); Estabeleceu loops de feedback (Comunidade → Equipes → Conselhos → Diretoria)",
  phase4Deliverable: "Estrutura de governança e processos operacionais",

  phase5: "ESCALAR",
  phase5Status: "Planejado",
  phase5Dates: "Jan 2026 - Dez 2027+",
  phase5Objective: "Expandir alcance, impacto e influência global",
  phase5Activities: "Criando materiais de comunicação para diferentes públicos; Escalar para 15+ Organizações Nacionais contribuindo ativamente; Crescer para 1.000+ usuários registrados; Lançar APIs públicas para pesquisadores acadêmicos",
  phase5Deliverable: "Autoridade global em desenvolvimento de liderança juvenil",

  phaseWhyTitle: "Por que Esta Abordagem Funciona",
  phaseWhy: "Cada fase se baseia na anterior. IDENTIFICAR estabelece o que estamos construindo. MEDIR garante rigor. PROMOVER engaja partes interessadas. DIRECIONAR garante governança. ESCALAR garante crescimento sustentável.",

  // Q6: Areas of Opportunity
  q6Title: "Quais são as 4 Áreas de Oportunidade da JCI?",
  areaIndividual: "Desenvolvimento Individual",
  areaIndividualDesc: "Jovens desenvolvem habilidades essenciais de liderança, confiança e crescimento pessoal através de treinamento, mentoria e experiência prática.",
  areaIndividualExamples: "Exemplos: Oratória, debate, cursos de liderança, certificação de habilidades",
  
  areaCommunity: "Impacto Comunitário",
  areaCommunityDesc: "Jovens líderes identificam necessidades comunitárias e projetam soluções que beneficiam a sociedade.",
  areaCommunityExamples: "Exemplos: Iniciativas de saúde, programas educacionais, socorro em desastres, projetos ambientais",
  
  areaInternational: "Cooperação Internacional",
  areaInternationalDesc: "Jovens líderes constroem redes globais, entendem perspectivas diversas e colaboram além das fronteiras.",
  areaInternationalExamples: "Exemplos: Parcerias de geminação JCI, prêmio TOYP, eventos internacionais, advocacia de direitos humanos",
  
  areaBusiness: "Negócios e Empreendedorismo",
  areaBusinessDesc: "Jovens líderes lançam negócios, impulsionam inovação e constroem oportunidade econômica.",
  areaBusinessExamples: "Exemplos: Jovem Empreendedor Criativo, JCI nos Negócios, aceleradoras de startups",

  // Q7: Connection between Areas and Observatory
  q7Title: "Qual é a conexão entre as 4 Áreas e o Observatório?",
  q7Answer: "O Observatório mede o contexto externo em que essas 4 Áreas operam.",
  q7Detail: "O YFI rastreia: Saúde Mental (afeta todas as 4 Áreas — mentes saudáveis aprendem melhor, constroem melhor), Força de Trabalho e Educação (suporta áreas de Desenvolvimento Individual + Negócios), Empreendedorismo (suporta área de Negócios).",
  q7Example: "Quando o Observatório mostra que África e Oriente Médio tem pontuação YFI de 53 (vs. Europa em 75), está dizendo: 'Jovens nessas regiões enfrentam lacunas de saúde mental, emprego e oportunidade de negócios. É aqui que as 4 Áreas de Oportunidade da JCI podem ter máximo impacto.'",
  q7Summary: "As 4 Áreas são como a JCI desenvolve líderes. O Observatório mostra onde e por que isso mais importa.",

  // Q8: Data-Driven Innovation
  q8Title: "O que é Inovação Baseada em Dados (o 4º Objetivo Estratégico)?",
  q8Answer: "O 4º Objetivo Estratégico aborda questões críticas: Como sabemos se nosso desenvolvimento de liderança está realmente funcionando? Como melhoramos? Onde devemos focar?",
  q8Means: "Inovação Baseada em Dados significa:",
  q8Means1: "Medir o que importa — Usar dados rigorosos sobre oportunidade para jovens, membros da JCI e impacto",
  q8Means2: "Analisar para insights — Encontrar padrões (ex: 'acesso à saúde mental varia 22 pontos entre regiões')",
  q8Means3: "Tomar decisões informadas — Alocar recursos com base em evidências",
  q8Means4: "Melhoria contínua — Coletar feedback, ajustar estratégia, medir resultados",
  q8Conclusion: "O Observatório transforma a JCI de uma organização que sente que faz diferença para uma que pode provar e medir seu impacto usando dados verificados e auditáveis.",

  // Section 3: Data and Sources
  dataSourcesTitle: "Dados e Fontes",

  // Q9: What data sources?
  q9Title: "Quais fontes de dados o Observatório usa?",
  primaryDataTitle: "Dados Primários da JCI",
  primaryData1: "Relatório de Impacto da JCI 2025 — 147.670 membros, 4.641 organizações locais, 1.000+ projetos anuais, 21M+ beneficiários",
  primaryData2: "Visão 2040 da JCI — Roteiro estratégico, metas de crescimento, plano de inovação tecnológica",
  primaryData3: "Pesquisa de Experiência de Membros 2025 — 3.767 respondentes em todas as 4 regiões",
  
  secondaryDataTitle: "Dados Globais Secundários",
  secondaryData1: "Atlas de Saúde Mental da OMS 2024 — Cobertura de serviços de saúde mental, estimativas de prevalência",
  secondaryData2: "Base de Dados de Mortalidade da OMS — Taxas de mortalidade por suicídio por país",
  secondaryData3: "Estado Mental do Mundo Sapien Labs 2024 — Pesquisa de bem-estar subjetivo",
  secondaryData4: "Tendências Globais de Emprego da OIT 2024-25 — Desemprego juvenil, estatísticas NEET",
  secondaryData5: "Instituto de Estatísticas da UNESCO — Matrículas educacionais, taxas de alfabetização",
  secondaryData6: "Banco Mundial — Índice de Capital Humano, Facilidade de Fazer Negócios",
  secondaryData7: "Monitor Global de Empreendedorismo GEM 2024-25 — Atividade empreendedora",
  secondaryData8: "Painel de Desenvolvimento Digital da UIT — Infraestrutura e acesso digital",
  
  dataTransparency: "Todas as fontes são públicas e verificadas. O Observatório contém zero dados proprietários, confidenciais ou restritos.",

  // Q10: Data quality
  q10Title: "Como a qualidade dos dados é garantida?",
  q10Answer: "Implementamos 7 critérios de qualidade inegociáveis:",
  qualityCriteria1: "Verificável — Identificação clara da fonte (ex: 'Atlas de Saúde Mental da OMS 2024, página 12')",
  qualityCriteria2: "Auditável — Metodologia completa documentada",
  qualityCriteria3: "Rastreável — Origem identificada, sem anonimato",
  qualityCriteria4: "Preciso — Validado antes da apresentação",
  qualityCriteria5: "Atual — Data clara e relevância temporal",
  qualityCriteria6: "Contextualizado — Limitações claramente explicadas",
  qualityCriteria7: "Citado — Crédito completo às fontes",

  validationProcessTitle: "Processo de Validação de 5 Passos",
  validationStep1: "Submissão (1-2 dias)",
  validationStep1Desc: "Recebimento de dados e verificação preliminar de completude",
  validationStep2: "Validação (5-7 dias)",
  validationStep2Desc: "Verificação técnica de metodologia e cálculos",
  validationStep3: "Auditoria (5-7 dias)",
  validationStep3Desc: "Auditoria independente de precisão dos dados",
  validationStep4: "Revisão por Pares (5-7 dias)",
  validationStep4Desc: "Revisão por especialistas no assunto",
  validationStep5: "Publicação (2-3 dias)",
  validationStep5Desc: "Certificação final e documentação de transparência",

  // Q11: Zero synthetic data
  q11Title: "O que significa 'zero dados sintéticos'?",
  q11Answer: "Zero dados sintéticos significa: Cada número que você vê rastreia para uma fonte de dados real. Nenhum número é inventado, estimado ou gerado por modelos.",
  
  notIncludedTitle: "NÃO incluímos",
  notIncluded1: "Projeções apresentadas como fatos atuais",
  notIncluded2: "Estatísticas geradas por IA",
  notIncluded3: "Valores interpolados preenchendo lacunas de dados",
  notIncluded4: "Estimativas baseadas em modelos (a menos que explicitamente rotuladas)",
  notIncluded5: "Distribuições assumidas",
  
  includedTitle: "Incluímos",
  included1: "Contagens reais (ex: '147.670 membros')",
  included2: "Estatísticas baseadas em pesquisas (ex: '3.767 respondentes')",
  included3: "Índices publicados (ex: 'pontuação YFI: 75,28')",
  included4: "Cenários explicitamente marcados (ex: 'Visão 2040: 500.000 membros' — claramente rotulado como meta estratégica)",
  
  zeroSyntheticWhy: "Por que isso importa: Você pode confiar nesses números. Eles não são gerados por algoritmos ou estimados por consultores. São fatos verificados de fontes auditáveis.",

  // Section 4: Using the Observatory
  usingTitle: "Usando o Observatório",

  // Q12: Update cycles
  q12Title: "Com que frequência os dados são atualizados?",
  annualUpdatesTitle: "Atualizações Anuais (Abrangentes)",
  annualUpdates1: "Novembro-Dezembro: Coleta de dados de todas as fontes",
  annualUpdates2: "Janeiro-Fevereiro: Validação e análise comparativa",
  annualUpdates3: "Março: Publicação da nova edição",
  annualUpdates4: "Abril-Maio: Feedback e engajamento de partes interessadas",
  
  intermediateUpdatesTitle: "Atualizações Intermediárias (Conforme Necessário)",
  intermediateUpdates1: "Novas descobertas significativas de pesquisa",
  intermediateUpdates2: "Mudanças organizacionais importantes ou inovações de programa",
  intermediateUpdates3: "Desenvolvimentos importantes de políticas",
  intermediateUpdates4: "Correções de erros ou esclarecimentos",
  
  continuousUpdatesTitle: "Atualizações Contínuas (Em Andamento)",
  continuousUpdates1: "Atualizações de links e correções de referências",
  continuousUpdates2: "Melhorias técnicas da plataforma",
  continuousUpdates3: "Melhorias na interface do usuário",
  continuousUpdates4: "Melhorias de acessibilidade",
  
  updateGoal: "O objetivo é manter o Observatório atual e preciso sem sobrecarregar os usuários com micro-atualizações constantes.",

  // Q13: Using the data
  q13Title: "Posso usar esses dados para minha pesquisa ou organização?",
  q13Answer: "Sim, absolutamente. O Observatório foi projetado para:",
  
  useNationalTitle: "Organizações Nacionais da JCI",
  useNational1: "Comparar seu progresso contra dados globais",
  useNational2: "Identificar onde seus esforços regionais de liderança têm maior impacto",
  useNational3: "Tomar decisões estratégicas baseadas em dados sobre onde expandir",
  
  useLocalTitle: "Organizações Locais da JCI",
  useLocal1: "Entender a paisagem de oportunidade para jovens em sua cidade/região",
  useLocal2: "Conectar seus projetos locais a desafios globais de oportunidade",
  useLocal3: "Rastrear se seu trabalho está abordando lacunas prioritárias",
  
  useAcademicTitle: "Pesquisadores Acadêmicos",
  useAcademic1: "Acesso a dados verificados e metodologia publicada",
  useAcademic2: "APIs para acesso programático (planejado para 2026)",
  useAcademic3: "Permissão para citar e desenvolver sobre descobertas do Observatório",
  
  usePolicyTitle: "Formuladores de Políticas e Governo",
  usePolicy1: "Evidências sobre oportunidade para jovens em seu país",
  usePolicy2: "Comparação com regiões vizinhas",
  usePolicy3: "Inteligência estratégica para políticas de educação, emprego, saúde mental",
  
  useCorporateTitle: "Parceiros Corporativos",
  useCorporate1: "Entender necessidades de desenvolvimento juvenil em seus mercados",
  useCorporate2: "Identificar onde parcerias com a JCI poderiam ter maior impacto",
  useCorporate3: "Acessar dados verificados para relatórios de responsabilidade corporativa",
  
  publicAPIs: "APIs Públicas — Lançamento no 2º trimestre de 2026 para pesquisadores e desenvolvedores",

  // Q14: Reproducibility
  q14Title: "Pesquisadores podem reproduzir a metodologia?",
  q14Answer: "Sim, completamente. Todos os componentes estão documentados e publicamente disponíveis:",
  q14Formula: "Fórmula do YFI (Publicada): YFI = (0,40 × SM) + (0,30 × FT) + (0,30 × EMP)",
  q14Sources: "Dados Fonte (Todos Públicos): Atlas de Saúde Mental da OMS, Base ILOSTAT da OIT, estatísticas UNESCO, indicadores Banco Mundial, Monitor GEM, Relatório de Impacto JCI 2025",
  q14Methodology: "Documentação Completa da Metodologia — Publicada integralmente na página de Metodologia",
  q14Commitment: "Qualquer pesquisador com acesso a essas mesmas fontes pode replicar a análise, verificar nossos cálculos e testar nossas conclusões. Esse é o compromisso com a transparência.",

  // Section 5: Future Development
  futureTitle: "Desenvolvimento Futuro",

  // Q15: Future roadmap
  q15Title: "Qual é o futuro do Observatório?",
  q15Answer: "O Observatório está evoluindo através de um plano de expansão faseado:",
  
  roadmap2026Title: "2026 (BETA — Expansão)",
  roadmap2026Items: "Escalar para 15+ Organizações Nacionais como contribuintes ativos; Crescer comunidade de usuários para 200+ membros; Lançar APIs públicas para pesquisadores acadêmicos; Desenvolver painel de análise avançada; Iniciar documentação de influência em políticas",
  roadmap2026Target: "Metas: 200+ usuários, 10+ artigos de mídia anuais citando o Observatório",
  
  roadmap2027Title: "2027+ (ESCALAR — Autoridade Global)",
  roadmap2027Items: "Posicionar como referência global para desenvolvimento de liderança juvenil; Integração com plataformas da ONU (ECOSOC), WEF, UNESCO; Influência visível em políticas em 10+ países; Comunidade global de 1.000+ usuários engajados; Publicação anual reconhecida em principais veículos de mídia",
  roadmap2027Target: "Metas: 500+ artigos de mídia anuais, influência em políticas em 10+ países",
  
  innovationsTitle: "Inovações Estratégicas Planejadas",
  innovation2026: "2026: Diretório de Mentores, Páginas de Parcerias, Contador de Advocacia em Tempo Real",
  innovation2027: "2027+: Newsletter Mensal de Pesquisa, Série de Webinars com Especialistas, Ferramentas Interativas de Avaliação de Habilidades, Calculadora de Correspondência de Subsídios",

  // Q16: Contributing
  q16Title: "Como posso contribuir para o Observatório?",
  
  contributeNationalTitle: "Organizações Nacionais podem contribuir",
  contributeNational: "Análise e insights regionais; Estudos de caso de programas bem-sucedidos; Dados de eventos e programas nacionais; Feedback sobre como as descobertas se aplicam ao seu contexto",
  
  contributeLocalTitle: "Organizações Locais podem contribuir",
  contributeLocal: "Resultados de projetos e métricas de impacto; Histórias de desenvolvimento de membros; Dados sobre alcance comunitário e beneficiários; Lições aprendidas de iniciativas locais",
  
  contributeMemberTitle: "Membros Individuais podem contribuir",
  contributeMember: "Feedback sobre habilidades aprendidas através de programas da JCI; Histórias de como a JCI mudou sua trajetória de liderança; Dados sobre resultados de emprego; Sugestões de melhorias",
  
  contributeHowTitle: "Como contribuir",
  contributeHow1: "Visite a página de Contribuição",
  contributeHow2: "Revise as diretrizes de contribuição",
  contributeHow3: "Complete o formulário de contribuição com documentação necessária",
  contributeHow4: "Receba feedback em 5-7 dias",
  contributeHow5: "Seja reconhecido como contribuidor quando publicado",
  
  contributeIncentivesTitle: "Incentivos para contribuidores",
  contributeIncentive1: "Citação formal em todas as publicações do Observatório",
  contributeIncentive2: "Certificado digital de colaboração",
  contributeIncentive3: "Reconhecimento em redes sociais",
  contributeIncentive4: "Válido para CV acadêmico",
  contributeIncentive5: "Acesso a insights do Observatório antes do lançamento público",

  // Q17: Vision 2040 alignment
  q17Title: "Como isso se alinha com a Visão 2040 da JCI?",
  q17Goal: "Meta da Visão 2040: Empoderar 500.000 jovens líderes até 2040",
  q17Contribution: "Contribuição do Observatório: Inteligência estratégica para alcançar essa meta",
  
  vision2040PillarTitle: "Pilar da Visão 2040",
  vision2040RoleTitle: "Papel do Observatório",
  
  vision2040Pillar1: "Excelência em Desenvolvimento de Liderança",
  vision2040Role1: "Mapeia lacunas de habilidades (comunicação #1, pensamento crítico #2); identifica prioridades de treinamento",
  
  vision2040Pillar2: "Tecnologia e Inovação Digital",
  vision2040Role2: "Rastreia adoção digital (jci.cc: 13.351 usuários, 260K seguidores sociais); guia roteiro tecnológico",
  
  vision2040Pillar3: "Equidade e Inclusão",
  vision2040Role3: "Revela desigualdade regional de oportunidade (lacuna de 22 pontos no YFI); identifica onde o impacto é mais necessário",
  
  vision2040Pillar4: "Sustentabilidade e Clima",
  vision2040Role4: "Fornece contexto de oportunidade juvenil para alinhamento de políticas ambientais",
  
  vision2040QuestionsTitle: "Perguntas Estratégicas que o Observatório Responde",
  vision2040Q1: "Onde expandir? — África e Oriente Médio (YFI mais baixo = maior oportunidade de impacto)",
  vision2040Q2: "O que priorizar? — Saúde mental (40% do peso do YFI = maior diferenciador)",
  vision2040Q3: "Como escalar? — Tecnologia (meta de 80%+ de adoção até 2040)",
  vision2040Q4: "Medidas de sucesso? — Melhorar YFI global em 4-7 pontos; fechar lacuna da África e Oriente Médio em 50%",
  
  vision2040Summary: "O Observatório transforma a Visão 2040 de uma aspiração em uma estratégia baseada em evidências.",

  // Section 6: 2026 Plan of Action (EVOLVE)
  evolveTitle: "Plano de Ação 2026: EVOLVE",

  // Q21: EVOLVE connection
  q21Title: "Como o Observatório se relaciona com o Plano de Ação 2026?",
  q21Answer: "O Plano de Ação 2026 estabelece três princípios fundamentais: LEAD | CONNECT | EVOLVE. O Observatório é uma manifestação direta do princípio EVOLVE.",
  q21Quote: "'Precisamos LIDERAR com o foco que nossa causa merece, CONECTAR com a mudança que queremos criar e EVOLUIR com o mundo em rápida mudança ao nosso redor.'",
  
  evolveHow: "Como o Observatório Habilita EVOLVE:",
  
  evolve1Title: "Evoluindo Através da Tomada de Decisão Baseada em Dados",
  evolve1: "O Observatório fornece evidências mostrando quais habilidades são mais necessárias globalmente, onde o desenvolvimento de liderança tem maior impacto e como medir o sucesso através de indicadores de oportunidade para jovens.",
  evolve1Summary: "Sem dados, não podemos evoluir estrategicamente — só podemos reagir. O Observatório permite evolução proativa baseada em evidências.",
  
  evolve2Title: "Apoiando os 4 Pilares de Ação",
  actionPillar1: "Sustentabilidade Primeiro",
  actionPillar1Desc: "Rastreia saúde mental (40% do peso do YFI) e oportunidade juvenil de longo prazo, garantindo que o trabalho crie futuros sustentáveis",
  actionPillar2: "Construindo Liderança Global",
  actionPillar2Desc: "Mapeia onde o desenvolvimento de liderança é mais necessário; valida que programas da JCI desenvolvem habilidades que jovens priorizam",
  actionPillar3: "Negócios ao Redor do Mundo",
  actionPillar3Desc: "Fornece dados do ecossistema de empreendedorismo (30% do peso do YFI); identifica onde programas de Negócios da JCI têm maior impacto",
  actionPillar4: "Conectando Vidas e Culturas",
  actionPillar4Desc: "Mostra diferenças regionais em oportunidade juvenil (lacuna de 22 pontos no YFI), demonstrando por que a conexão intercultural importa",
  
  evolve3Title: "Medindo KPIs que Importam",
  evolve3: "O Observatório torna KPIs significativos conectando-os a dados externos de oportunidade juvenil. Por exemplo, se vemos 30% de crescimento na membresia na África e Oriente Médio, o Observatório mostra que isso importa porque aquela região tem o YFI mais baixo (53) e maior lacuna de oportunidade.",
  
  evolve4Title: "'De Volta ao Básico' Encontra Inovação Estratégica",
  evolve4: "O Observatório complementa a Fase 1 'De Volta ao Básico' do Plano 2026 fornecendo contexto estratégico para programas fundamentais, oferecendo dados de benchmarking para certificação de treinadores e apoiando Mentor-Mentee 2.0 com evidências.",
  
  evolve5Title: "Eventos Globais com Propósito",
  evolve5: "O Observatório fornece dados que tornam eventos globais (Cúpula de Liderança Juvenil, Semana de Sustentabilidade, Fóruns de Liderança Feminina) baseados em evidências em vez de cerimoniais.",
  
  evolveKeyConnection: "Conexão Chave: EVOLVE significa adaptar-se ao mundo em rápida mudança. O Observatório é como a JCI evolui — de liderança baseada em intuição para liderança baseada em dados, de 'achamos que fazemos diferença' para 'podemos provar nosso impacto.'",
  
  evolveSummary: "O Plano de Ação 2026 define a direção (LEAD | CONNECT | EVOLVE). O Observatório fornece a inteligência que torna a evolução estratégica, mensurável e auditável.",

  // Section 7: Common Questions
  commonTitle: "Perguntas Comuns",

  // Q22: Privacy
  q22Title: "Como o Observatório aborda privacidade e confidencialidade?",
  
  privacyMemberTitle: "Privacidade do Membro",
  privacyMember1: "Nenhum dado individual de membro é publicado",
  privacyMember2: "Todos os dados de pesquisa são agregados e anonimizados",
  privacyMember3: "Respondentes não podem ser identificados nos resultados",
  privacyMember4: "Informações pessoais são protegidas conforme política de privacidade",
  
  privacyOrgTitle: "Dados Organizacionais",
  privacyOrg: "Apenas dados agregados são publicados (ex: '147.670 membros')",
  
  privacySecurityTitle: "Segurança de Dados",
  privacySecurity1: "Transmissão e armazenamento criptografados",
  privacySecurity2: "Auditorias de segurança regulares",
  privacySecurity3: "Acesso limitado a dados brutos (apenas Equipe de Operações)",
  privacySecurity4: "Conformidade com LGPD e leis regionais de privacidade",

  // Q23: Disagreement
  q23Title: "E se eu discordar das descobertas do Observatório?",
  q23Answer: "Seu feedback é essencial. Discordância nos ajuda a melhorar.",
  q23Process: "Veja como:",
  q23Step1: "Envie feedback detalhado para tmariano@jci.cc",
  q23Step2: "Especifique sua preocupação (metodologia, precisão dos dados, interpretação)",
  q23Step3: "Seu feedback vai para o Conselho de Desenvolvimento relevante para revisão",
  q23Step4: "Em 2 semanas, explicaremos nossa metodologia ou reconheceremos seu ponto válido",
  q23Step5: "Se seu feedback revelar um erro, corrigimos e citamos você",
  q23Summary: "O Observatório evolui através de discordância construtiva. Não estamos tentando estar certos — estamos tentando ser precisos.",

  // Q24: Staying updated
  q24Title: "Como posso me manter atualizado sobre desenvolvimentos do Observatório?",
  
  newsletterTitle: "Newsletter (Lançamento 2º trimestre 2026)",
  newsletter1: "Destaques mensais de novas descobertas",
  newsletter2: "Destaque de contribuidores",
  newsletter3: "Próximas inovações",
  newsletter4: "Inscreva-se na página de Autenticação",
  
  webinarsTitle: "Webinars (Lançamento 3º trimestre 2026)",
  webinars1: "Explicações de especialistas sobre dimensões do Observatório",
  webinars2: "Como usar dados do Observatório para planejamento estratégico",
  webinars3: "Perguntas e respostas com Conselhos de Desenvolvimento",
  webinars4: "Gratuito, aberto a todos, multilíngue",
  
  directEmailTitle: "Email Direto",
  directEmail: "tmariano@jci.cc — Consultas gerais, perguntas sobre dados ou metodologia específicos",

  // Contact Section
  contactTitle: "Contato e Recursos",
  
  contactTechnicalTitle: "Para Perguntas Técnicas",
  contactTechnical: "tmariano@jci.cc",
  
  contactRegionalTitle: "Para Perguntas Regionais",
  contactAmericas: "América: adc@jci.cc",
  contactEurope: "Europa: edc@jci.cc",
  contactAfrica: "África e Oriente Médio: amdec@jci.cc",
  contactAsia: "Ásia e Pacífico: apdc@jci.cc",
  
  keyResourcesTitle: "Recursos Principais",
  keyResource1: "Metodologia Completa",
  keyResource2: "Painel de Dados",
  keyResource3: "Como Contribuir",
  
  accessibilityTitle: "Acessibilidade",
  accessibility1: "Disponível em Inglês, Português, Espanhol, Francês e Japonês",
  accessibility2: "PDFs baixáveis de todo conteúdo (em breve)",
  accessibility3: "Compatível com leitores de tela",
  accessibility4: "Idiomas adicionais sob solicitação",

  // CTA
  ctaTitle: "Pronto para Explorar?",
  ctaDescription: "O Observatório de Futuros da Juventude é construído sobre evidências. Nosso compromisso é com transparência, rigor e melhoria contínua em apoio à missão da JCI de desenvolver líderes para um mundo em mudança.",
  ctaExplore: "Explorar o Observatório",
  ctaContribute: "Contribuir para o Observatório",
  ctaContact: "Entre em Contato",

  // Source
  sourceAttribution: "Fonte: Plano Estratégico JCI 2023-2027, Metodologia DASHCity, Relatório de Impacto JCI 2025",
};